/**
 * TenantInstallationsTab — SAAS-COMPANIES-P2-INSTALLATIONS-TAB.
 *
 * Aba "Instalações" do detalhe da empresa. Reaproveita listInstallations
 * ({tenantId}) — /api/admin/installations com a ternária platform-only. READ-ONLY:
 * últimas instalações + filtro de status + link ao device. Sem mutações.
 */
import { useEffect, useMemo, useState } from "react";
import { listInstallations } from "../../lib/api.js";

const STATUS_LABEL = {
  draft: "Rascunho", pending_device: "Aguardando device",
  installed: "Instalada", bound: "Vinculada", failed: "Falhou",
  cancelled: "Cancelada",
};
const STATUS_TONE = {
  installed: "text-noc-ok", bound: "text-noc-ok",
  pending_device: "text-noc-warn", failed: "text-noc-err",
  cancelled: "text-noc-muted", draft: "text-noc-muted",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

export default function TenantInstallationsTab({ tenantId, onNavigate }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true); setErr(null);
    listInstallations({ tenantId, signal: ac.signal })
      .then((d) => setSessions(d.sessions || []))
      .catch((e) => { if (e?.name !== "AbortError") setErr(e?.message || "Falha ao carregar instalações."); })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [tenantId]);

  const statuses = useMemo(() => {
    const set = new Set(sessions.map((s) => s.status).filter(Boolean));
    return ["all", ...set];
  }, [sessions]);

  const visible = useMemo(
    () => statusFilter === "all" ? sessions : sessions.filter((s) => s.status === statusFilter),
    [sessions, statusFilter]);

  if (loading) return <div className="text-sm text-noc-muted glass-card-pad">Carregando instalações…</div>;
  if (err) return <div className="text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg p-3">{err}</div>;
  if (sessions.length === 0) {
    return <div className="glass-card-pad text-sm text-noc-muted text-center py-10">
      Nenhuma instalação registrada para esta empresa.
    </div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex rounded-lg border border-noc-border overflow-hidden text-xs w-fit flex-wrap">
        {statuses.map((k) => (
          <button key={k} type="button" onClick={() => setStatusFilter(k)}
            className={`px-2.5 py-1 ${statusFilter === k
              ? "bg-noc-accent/20 text-noc-accent font-semibold"
              : "text-noc-muted hover:text-noc-text"}`}>
            {k === "all" ? `Todas (${sessions.length})` : (STATUS_LABEL[k] || k)}
          </button>
        ))}
      </div>

      <div className="glass-card-pad overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-noc-muted text-xs border-b border-noc-border">
              <th className="py-2 pr-3">QR</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Cliente</th>
              <th className="py-2 pr-3">Técnico</th>
              <th className="py-2 pr-3">Data</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr key={s.id} className="border-b border-noc-border/50">
                <td className="py-2 pr-3 font-mono text-xs">{s.qr_serial || "—"}</td>
                <td className={`py-2 pr-3 text-xs ${STATUS_TONE[s.status] || "text-noc-muted"}`}>
                  {STATUS_LABEL[s.status] || s.status}
                </td>
                <td className="py-2 pr-3 text-noc-muted text-xs">{s.customer_id ? `#${s.customer_id}` : "—"}</td>
                <td className="py-2 pr-3 text-noc-muted text-xs">{s.installer || "—"}</td>
                <td className="py-2 pr-3 text-noc-muted text-xs">{fmtDate(s.created_at)}</td>
                <td className="py-2 text-right">
                  {s.device_id && (
                    <button type="button" onClick={() => onNavigate?.("detail", s.device_id)}
                      className="text-xs text-noc-accent border border-noc-accent/40 rounded px-2 py-1">
                      abrir device
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
